import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../../../../i18n/I18nContext';
import './FilterBar.css';

/**
 * FilterBar Component
 * Shared filter component for all report pages
 */
const FilterBar = ({
    onFilterChange,
    availableFilters = ['period', 'status', 'client'],
    savedViews = [],
    onSaveView,
    onDeleteView,
    onLoadView
}) => {
    const { t } = useI18n();

    const [filters, setFilters] = useState({
        period: 'last_month',
        status: 'all',
        client: 'all',
        driver: 'all',
        vehicle: 'all',
        subcontractor: 'all',
        product: 'all',
        zone: 'all',
        startDate: '',
        endDate: ''
    });

    const [showSaveViewModal, setShowSaveViewModal] = useState(false);
    const [newViewName, setNewViewName] = useState('');

    // Period options
    const periodOptions = [
        { value: 'today', label: 'Aujourd\'hui' },
        { value: 'yesterday', label: 'Hier' },
        { value: 'last_7_days', label: '7 derniers jours' },
        { value: 'last_30_days', label: '30 derniers jours' },
        { value: 'last_month', label: 'Mois dernier' },
        { value: 'this_month', label: 'Ce mois' },
        { value: 'last_quarter', label: 'Trimestre dernier' },
        { value: 'this_quarter', label: 'Ce trimestre' },
        { value: 'last_year', label: 'Année dernière' },
        { value: 'this_year', label: 'Cette année' },
        { value: 'custom', label: 'Période personnalisée' }
    ];

    // Status options
    const statusOptions = [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'EN_ATTENTE', label: 'En attente' },
        { value: 'EN_COURS', label: 'En cours' },
        { value: 'TERMINEE', label: 'Terminé' },
        { value: 'ANNULEE', label: 'Annulé' }
    ];

    // Update parent when filters change
    useEffect(() => {
        if (onFilterChange) {
            onFilterChange(filters);
        }
    }, [filters, onFilterChange]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleClearFilters = () => {
        setFilters({
            period: 'last_month',
            status: 'all',
            client: 'all',
            driver: 'all',
            vehicle: 'all',
            subcontractor: 'all',
            product: 'all',
            zone: 'all',
            startDate: '',
            endDate: ''
        });
    };

    const handleSaveView = () => {
        if (newViewName.trim() && onSaveView) {
            onSaveView({
                name: newViewName,
                filters: filters
            });
            setNewViewName('');
            setShowSaveViewModal(false);
        }
    };

    const handleLoadView = (view) => {
        if (view && view.filters) {
            setFilters(view.filters);
            if (onLoadView) {
                onLoadView(view);
            }
        }
    };

    return (
        <div className="filter-bar">
            <div className="filter-bar-main">
                {/* Currency Filter */}
                {availableFilters.includes('currency') && (
                    <div className="filter-group">
                        <label className="filter-label">
                            <i className="bi bi-cash-coin me-2"></i>
                            Devise
                        </label>
                        <select
                            className="filter-select"
                            value={filters.currency || 'EUR'}
                            onChange={(e) => handleFilterChange('currency', e.target.value)}
                        >
                            <option value="EUR">Euro (€)</option>
                            <option value="MAD">Dirham (MAD)</option>
                        </select>
                    </div>
                )}

                {/* Period Filter */}
                {availableFilters.includes('period') && (
                    <div className="filter-group">
                        <label className="filter-label">
                            <i className="bi bi-calendar3 me-2"></i>
                            Période
                        </label>
                        <select
                            className="filter-select"
                            value={filters.period}
                            onChange={(e) => handleFilterChange('period', e.target.value)}
                        >
                            {periodOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Custom Date Range */}
                {filters.period === 'custom' && (
                    <>
                        <div className="filter-group">
                            <label className="filter-label">Date début</label>
                            <input
                                type="date"
                                className="filter-input"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">Date fin</label>
                            <input
                                type="date"
                                className="filter-input"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            />
                        </div>
                    </>
                )}

                {/* Status Filter */}
                {availableFilters.includes('status') && (
                    <div className="filter-group">
                        <label className="filter-label">
                            <i className="bi bi-flag me-2"></i>
                            Statut
                        </label>
                        <select
                            className="filter-select"
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Client Filter */}
                {availableFilters.includes('client') && (
                    <div className="filter-group">
                        <label className="filter-label">
                            <i className="bi bi-building me-2"></i>
                            Client
                        </label>
                        <select
                            className="filter-select"
                            value={filters.client}
                            onChange={(e) => handleFilterChange('client', e.target.value)}
                        >
                            <option value="all">Tous les clients</option>
                            {/* Client options will be populated from API */}
                        </select>
                    </div>
                )}

                {/* Driver Filter */}
                {availableFilters.includes('driver') && (
                    <div className="filter-group">
                        <label className="filter-label">
                            <i className="bi bi-person me-2"></i>
                            Conducteur
                        </label>
                        <select
                            className="filter-select"
                            value={filters.driver}
                            onChange={(e) => handleFilterChange('driver', e.target.value)}
                        >
                            <option value="all">Tous les conducteurs</option>
                            {/* Driver options will be populated from API */}
                        </select>
                    </div>
                )}

                {/* Vehicle Filter */}
                {availableFilters.includes('vehicle') && (
                    <div className="filter-group">
                        <label className="filter-label">
                            <i className="bi bi-truck me-2"></i>
                            Véhicule
                        </label>
                        <select
                            className="filter-select"
                            value={filters.vehicle}
                            onChange={(e) => handleFilterChange('vehicle', e.target.value)}
                        >
                            <option value="all">Tous les véhicules</option>
                            {/* Vehicle options will be populated from API */}
                        </select>
                    </div>
                )}
            </div>


        </div>
    );
};

FilterBar.propTypes = {
    onFilterChange: PropTypes.func,
    availableFilters: PropTypes.arrayOf(PropTypes.string),
    savedViews: PropTypes.array,
    onSaveView: PropTypes.func,
    onDeleteView: PropTypes.func,
    onLoadView: PropTypes.func
};

export default FilterBar;
